import { useEffect, useRef, useState } from 'react'
import { Button } from '@/common/ui/button'
import { Input } from '@/common/ui/input'
import { toast } from 'sonner'
import showErrorToast from '@/common/utils/showErrorToast'
import { Trash2, ImageIcon } from 'lucide-react'
import { useQueryClient } from '@tanstack/react-query'
import Cropper from 'react-easy-crop'
import type { Point, Area } from 'react-easy-crop'
import { useUploadImage } from '@/common/hooks/uploadImage'
import Spinner from '@/common/components/Spinner'
import useHasPermission from '@/common/hooks/useHasPermission'
import { PermissionKeys } from '@/modules/manage-member/mappers'
import { OperatorGroupPermission } from '@/schema'

interface ImageItem {
	id: string
	imageData: string
	crop: Point
	zoom: number
	link: string
	fileUrl?: string
	order: number
}

interface PendingImage {
	imageData: string
	fileUrl: string
	link: string
	crop: Point
	zoom: number
}

interface PendingUpdate {
	id: string
	link: string
	order: number
}

interface ImageUploadFormProps {
	title: string
	uploadButtonText: string
	uploadButtonHeight: string
	cropperHeight: string
	cropperAspect: number
	items:
		| Array<{
				id: string
				image: string
				link?: string
				order?: number
		  }>
		| undefined
	isLoading: boolean
	queryKey: string[]
	createItem: (payload: {
		payload: { image: string; link: string }
	}) => Promise<{ id: string }>
	updateItem: (params: { id: string; payload: any }) => void
	deleteItem: (params: { id: string; itemId: string }) => void
	getUploadFileUrl: (params: {
		payload: { fileExtension: string }
	}) => Promise<{
		presignedUrl: string
		fileUrl: string
	}>
	permissionKey?: OperatorGroupPermission
}

// Function to create an image from a url
const createImage = (url: string): Promise<HTMLImageElement> =>
	new Promise((resolve, reject) => {
		const image = new Image()
		image.crossOrigin = 'anonymous'
		image.addEventListener('load', () => resolve(image))
		image.addEventListener('error', (error) => reject(error))
		// Add a timestamp to bust cache and force CORS headers
		const proxyUrl = `${url}${url.includes('?') ? '&' : '?'}_t=${Date.now()}`
		image.src = proxyUrl
	})

// Function to get the cropped image
async function getCroppedImg(
	imageSrc: string,
	pixelCrop: Area,
	rotation = 0,
): Promise<string> {
	try {
		const image = await createImage(imageSrc)
		const canvas = document.createElement('canvas')
		const ctx = canvas.getContext('2d')

		if (!ctx) {
			throw new Error('No 2d context')
		}

		// Set canvas size to match the desired crop size
		canvas.width = pixelCrop.width
		canvas.height = pixelCrop.height

		// Draw rotated image
		ctx.translate(canvas.width / 2, canvas.height / 2)
		ctx.rotate((rotation * Math.PI) / 180)
		ctx.translate(-canvas.width / 2, -canvas.height / 2)

		// Draw the image with crop
		ctx.drawImage(
			image,
			pixelCrop.x,
			pixelCrop.y,
			pixelCrop.width,
			pixelCrop.height,
			0,
			0,
			pixelCrop.width,
			pixelCrop.height,
		)

		return canvas.toDataURL('image/webp')
	} catch (error) {
		console.error('Error in getCroppedImg:', error)
		throw error
	}
}

// Function to process image URL to handle CORS
const processImageUrl = (url: string) => {
	return `${url}${url.includes('?') ? '&' : '?'}_t=${Date.now()}`
}

export default function ImageUploadForm({
	title,
	uploadButtonText,
	uploadButtonHeight,
	cropperHeight,
	cropperAspect,
	items,
	isLoading,
	queryKey,
	createItem,
	updateItem,
	deleteItem,
	getUploadFileUrl,
	permissionKey = PermissionKeys.WEB_SETTING as OperatorGroupPermission,
}: ImageUploadFormProps) {
	const queryClient = useQueryClient()
	const fileInputRef = useRef<HTMLInputElement>(null)
	const { uploadImage, isUploading } = useUploadImage(getUploadFileUrl)
	const hasWritePermission = useHasPermission(permissionKey)

	const [images, setImages] = useState<ImageItem[]>([])

	useEffect(() => {
		if (items) {
			setImages(
				items.map((item) => ({
					id: item.id,
					imageData: item.image,
					crop: { x: 0, y: 0 },
					zoom: 1,
					link: item.link || '',
					order: item.order || 0,
				})),
			)
		}
	}, [items])

	const [rotation, setRotation] = useState(0)
	const [pendingImage, setPendingImage] = useState<PendingImage | null>(null)
	const [pendingUpdate, setPendingUpdate] = useState<PendingUpdate | null>(null)
	const [croppedAreaPixels, setCroppedAreaPixels] = useState<Area | null>(null)

	const handleGifUpload = async (file: File) => {
		// Load image to check dimensions
		const img = new Image()
		const objectUrl = URL.createObjectURL(file)

		img.onload = async () => {
			URL.revokeObjectURL(objectUrl)

			const imageRatio = img.width / img.height
			const tolerance = 0.01 // Allow small rounding differences

			// Check if aspect ratio matches
			if (Math.abs(imageRatio - cropperAspect) > tolerance) {
				const ratioText =
					cropperAspect === 1 ? '1:1' : `${Math.round(cropperAspect * 100)}:100`
				showErrorToast(
					`GIF ต้องมีอัตราส่วน ${ratioText} (เช่น ${Math.round(1000 * cropperAspect)}x1000 หรือ ${Math.round(500 * cropperAspect)}x500) \nไฟล์ที่อัพโหลด: ${img.width}x${img.height}`,
				)
				return
			}

			// Dimensions match, upload directly
			try {
				const fileUrl = await uploadImage(file)
				if (fileUrl) {
					const response = await createItem({
						payload: {
							image: fileUrl,
							link: '',
						},
					})

					setImages((prev) => [
						...prev,
						{
							id: response.id,
							imageData: fileUrl,
							crop: { x: 0, y: 0 },
							zoom: 1,
							link: '',
							fileUrl: fileUrl,
							order: 0,
						},
					])

					queryClient.invalidateQueries({
						queryKey,
					})

					toast.success(`เพิ่ม${title}สำเร็จ`)
				}
			} catch (error) {
				showErrorToast(error as Error)
			}
		}

		img.onerror = () => {
			URL.revokeObjectURL(objectUrl)
			showErrorToast('ไม่สามารถอ่านไฟล์ GIF ได้')
		}

		img.src = objectUrl
	}

	const handleFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
		if (e.target.files && e.target.files[0]) {
			const file = e.target.files[0]

			// Check if file is GIF
			if (file.type === 'image/gif') {
				await handleGifUpload(file)
			} else {
				try {
					const fileUrl = await uploadImage(file)
					if (fileUrl) {
						const processedUrl = processImageUrl(fileUrl)
						setPendingImage({
							imageData: processedUrl,
							fileUrl,
							link: '',
							crop: { x: 0, y: 0 },
							zoom: 1,
						})
					}
				} catch (error) {
					showErrorToast(error as Error)
				}
			}
		}
	}

	const handleUploadClick = () => {
		fileInputRef.current?.click()
	}

	const handleCropComplete = (_: Area, croppedAreaPixels: Area) => {
		setCroppedAreaPixels(croppedAreaPixels)
	}

	const handleConfirmUpload = async () => {
		console.log('Starting handleConfirmUpload')
		if (!pendingImage || !croppedAreaPixels) {
			console.log('Missing required data:', { pendingImage, croppedAreaPixels })
			return
		}

		try {
			console.log('Starting crop process with:', {
				imageData: pendingImage.imageData,
				croppedAreaPixels,
				rotation,
			})

			// Get the cropped image
			const croppedImage = await getCroppedImg(
				pendingImage.imageData,
				croppedAreaPixels,
				rotation,
			)
			console.log('Got cropped image data')

			// Convert base64 to blob
			const response = await fetch(croppedImage)
			const blob = await response.blob()
			const file = new File([blob], 'cropped-image.webp', {
				type: 'image/webp',
			})
			console.log('Converted to file')

			// Upload the cropped image
			const croppedFileUrl = await uploadImage(file)
			console.log('Uploaded cropped image:', croppedFileUrl)

			if (croppedFileUrl) {
				const response = await createItem({
					payload: {
						image: croppedFileUrl,
						link: pendingImage.link,
					},
				})
				console.log('Created item with response:', response)

				setImages((prev) => [
					...prev,
					{
						id: response.id,
						imageData: croppedFileUrl,
						crop: pendingImage.crop,
						zoom: pendingImage.zoom,
						link: pendingImage.link,
						fileUrl: croppedFileUrl,
						order: 0,
					},
				])

				setPendingImage(null)
				queryClient.invalidateQueries({
					queryKey,
				})

				toast.success(`เพิ่ม${title}สำเร็จ`)
			}
		} catch (error) {
			console.error('Error in handleConfirmUpload:', error)
			showErrorToast(`เกิดข้อผิดพลาดในการสร้าง${title}`)
		}
	}

	const handlePendingChange = (field: string, value: string) => {
		if (pendingImage) {
			setPendingImage({ ...pendingImage, [field]: value })
		}
	}

	const handleUpdateClick = (id: string, link: string, order: number) => {
		const currentItem = images.find((img) => img.id === id)
		if (currentItem) {
			setPendingUpdate({
				id,
				link: link || currentItem.link,
				order: order || currentItem.order,
			})
		}
	}

	const handleConfirmUpdate = () => {
		if (!pendingUpdate) return

		const payload = {
			link: pendingUpdate.link,
			order: pendingUpdate.order,
		}

		updateItem({
			id: pendingUpdate.id,
			payload,
		})

		setImages((prev) =>
			prev.map((img) =>
				img.id === pendingUpdate.id
					? {
							...img,
							link: pendingUpdate.link,
							order: pendingUpdate.order,
						}
					: img,
			),
		)
		setPendingUpdate(null)
		queryClient.invalidateQueries({
			queryKey,
		})
	}

	const handleDelete = (id: string) => {
		deleteItem({ id, itemId: id })
		setImages((prev) => prev.filter((img) => img.id !== id))
		queryClient.invalidateQueries({
			queryKey,
		})
	}

	if (isLoading) return <Spinner />

	return (
		<div className="space-y-8">
			<div className="space-y-4">
				<div className="flex items-center justify-between">
					<h2 className="text-2xl font-bold">จัดการ{title}</h2>
				</div>

				<Button
					type="button"
					variant="outline"
					onClick={handleUploadClick}
					style={{ height: uploadButtonHeight }}
					className="w-full"
					disabled={!hasWritePermission}
				>
					<ImageIcon className="mr-2 h-8 w-8" />
					{uploadButtonText}
				</Button>

				<Input
					type="file"
					ref={fileInputRef}
					onChange={handleFileChange}
					className="hidden"
					accept="image/*"
					disabled={!hasWritePermission}
				/>
			</div>

			{pendingImage && (
				<div className="space-y-4 rounded-lg border p-4">
					<div
						style={{ height: cropperHeight }}
						className="relative w-full overflow-hidden rounded-lg"
					>
						<Cropper
							image={pendingImage.imageData}
							crop={pendingImage.crop}
							rotation={rotation}
							zoom={pendingImage.zoom}
							aspect={cropperAspect}
							onRotationChange={setRotation}
							onCropComplete={(croppedArea, croppedAreaPixels) => {
								handleCropComplete(croppedArea, croppedAreaPixels)
							}}
							onCropChange={(crop) =>
								setPendingImage({ ...pendingImage, crop })
							}
							onZoomChange={(zoom) =>
								setPendingImage({ ...pendingImage, zoom })
							}
						/>
					</div>
					<div className="flex flex-col gap-4">
						<div>
							<p className="mb-2 text-sm font-medium">ลิ้งค์</p>
							<Input
								placeholder="URL (ถ้ามี)"
								value={pendingImage.link}
								onChange={(e) => handlePendingChange('link', e.target.value)}
								disabled={!hasWritePermission}
							/>
						</div>
						<div className="flex gap-2">
							<Button
								onClick={handleConfirmUpload}
								disabled={isUploading || !hasWritePermission}
							>
								{isUploading ? 'กำลังอัพโหลด...' : 'ยืนยันการอัพโหลด'}
							</Button>
							<Button variant="outline" onClick={() => setPendingImage(null)}>
								ยกเลิก
							</Button>
						</div>
					</div>
				</div>
			)}

			<div className="grid grid-cols-1 gap-6">
				{images.map((item) => (
					<div
						key={item.id}
						className="relative space-y-4 rounded-lg border p-4"
					>
						<Button
							variant="destructive"
							size="icon"
							onClick={() => handleDelete(item.id)}
							className="absolute right-4 top-4 z-10"
							disabled={!hasWritePermission}
						>
							<Trash2 className="size-4" />
						</Button>

						<div
							style={{ height: cropperHeight }}
							className="relative w-full overflow-hidden rounded-lg"
						>
							<img
								src={item.imageData}
								alt=""
								className="h-full w-full object-contain"
							/>
						</div>
						<div className="flex flex-col gap-4">
							<div className="flex gap-4">
								<div className="flex-1">
									<p className="mb-2 text-sm font-medium">ลิ้งค์</p>
									<Input
										placeholder="URL (ถ้ามี)"
										value={
											pendingUpdate?.id === item.id
												? pendingUpdate.link
												: item.link
										}
										onChange={(e) => {
											if (pendingUpdate?.id === item.id) {
												setPendingUpdate({
													...pendingUpdate,
													link: e.target.value,
												})
											} else {
												handleUpdateClick(item.id, e.target.value, item.order)
											}
										}}
										disabled={!hasWritePermission}
									/>
								</div>
								<div>
									<p className="mb-2 text-sm font-medium">ลำดับ</p>
									<Input
										type="number"
										placeholder="ลำดับ"
										className="w-24"
										value={
											pendingUpdate?.id === item.id
												? pendingUpdate.order === 0
													? ''
													: pendingUpdate.order
												: item.order === 0
													? ''
													: item.order
										}
										onChange={(e) => {
											const value =
												e.target.value === '' ? 0 : parseInt(e.target.value)
											if (pendingUpdate?.id === item.id) {
												setPendingUpdate({
													...pendingUpdate,
													order: value,
												})
											} else {
												handleUpdateClick(item.id, item.link, value)
											}
										}}
										disabled={!hasWritePermission}
									/>
								</div>
							</div>
							<div className="flex gap-2">
								<Button
									onClick={handleConfirmUpdate}
									disabled={
										!pendingUpdate ||
										pendingUpdate.id !== item.id ||
										!hasWritePermission
									}
									className="w-fit"
									variant="default"
								>
									ยืนยันการแก้ไข
								</Button>
							</div>
						</div>
					</div>
				))}
			</div>
		</div>
	)
}
